import { useEffect, useMemo, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import { Repeat, Clock, Check } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { isDoneThisPeriod } from '@/lib/recurrence'
import { isPic } from '@/lib/utils'
import type { Recurrence } from '@/lib/types'

// Section "Tugas Rutin Saya" di bawah kanban My Dashboard.
// - Hanya item rutin yang di-assign ke user login (fallback PIC project).
// - "Checked" dihitung per-periode (WIB) dari tanggalSelesai → otomatis reset
//   tampilannya di pergantian hari/minggu/bulan, tanpa menunggu cron.
// - Banner amber menghitung item yang masih pending periode ini.

const REC_ORDER: Recurrence[] = ['daily', 'weekly', 'monthly']
const REC_LABEL: Record<Recurrence, string> = { daily: 'Harian', weekly: 'Mingguan', monthly: 'Bulanan' }

interface Row {
  key: string
  kind: 'task' | 'subtask'
  id: string
  title: string
  projectName: string
  projectColor: string
  recurrence: Recurrence
  checked: boolean
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

export function RecurringTasksSection() {
  const { projects, tasks, subtasks, updateTask, toggleSubtask } = useApp()
  const { data: session } = useSession()
  const me = norm(session?.user?.username)

  // Tick tiap 60 dtk supaya status "periode ini" ikut akurat saat hari berganti
  // selagi halaman terbuka.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const groups = useMemo(() => {
    if (!me) return null

    const projOf = (id: string) => projects.find((p) => p.id === id)
    const rows: Row[] = []

    // ── Task rutin milik saya ──
    for (const t of tasks) {
      if (!t.recurrence) continue
      const p = projOf(t.projectId)
      if (!p) continue
      const mine = t.assignee ? isPic(t.assignee, me) : norm(p.pic) === me
      if (!mine) continue
      rows.push({
        key: `task-${t.id}`, kind: 'task', id: t.id, title: t.title,
        projectName: p.name, projectColor: p.color, recurrence: t.recurrence,
        checked: t.status === 'done' && isDoneThisPeriod(t.recurrence, t.tanggalSelesai, now),
      })
    }

    // ── Subtask rutin milik saya ──
    for (const s of subtasks) {
      if (!s.recurrence) continue
      const t = tasks.find((x) => x.id === s.taskId)
      if (!t) continue
      const p = projOf(t.projectId)
      if (!p) continue
      const pics = (s.assignee ?? '').split(';').map(norm).filter(Boolean)
      const mine = pics.length
        ? pics.includes(me)
        : (t.assignee ? isPic(t.assignee, me) : norm(p.pic) === me)
      if (!mine) continue
      rows.push({
        key: `subtask-${s.id}`, kind: 'subtask', id: s.id, title: s.title,
        projectName: p.name, projectColor: p.color, recurrence: s.recurrence,
        checked: s.completed && isDoneThisPeriod(s.recurrence, s.tanggalSelesai, now),
      })
    }

    const total = rows.length
    if (total === 0) return null

    const pending = rows.filter((r) => !r.checked).length
    const byRec = REC_ORDER
      .map((rec) => ({
        rec,
        items: rows
          .filter((r) => r.recurrence === rec)
          .sort((a, b) => Number(a.checked) - Number(b.checked) || a.title.localeCompare(b.title)),
      }))
      .filter((g) => g.items.length > 0)

    return { byRec, total, pending }
  }, [me, projects, tasks, subtasks, now])

  if (!groups) return null

  const toggle = (r: Row) => {
    if (r.kind === 'task') {
      updateTask(r.id, r.checked
        ? { status: 'in-progress', tanggalSelesai: null }
        : { status: 'done', tanggalSelesai: new Date().toISOString() })
    } else {
      toggleSubtask(r.id, !r.checked)
    }
  }

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 px-1 mb-3">
        <Repeat className="w-4 h-4 text-[#6366F1]" />
        <span className="font-display text-[13px] font-bold text-[#1A1916]">Tugas Rutin Saya</span>
        <span className="text-[11px] font-body text-[#AEADA8]">{groups.total}</span>
      </div>

      {groups.pending > 0 && (
        <div
          role="status"
          className="flex items-center gap-2 mb-4 px-3.5 py-2.5 rounded-xl bg-[#FFF8EB] border border-[#F5D88E] text-[#92500A]"
        >
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="text-[12.5px] font-body font-semibold">
            {groups.pending} tugas rutin menunggu dikerjakan
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {groups.byRec.map((g) => (
          <div key={g.rec} className="bg-white border border-[#E8E7E3] rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="font-display text-[11px] font-bold uppercase tracking-wider text-[#6B6860]">
                {REC_LABEL[g.rec]}
              </span>
              <span className="text-[11px] font-body text-[#AEADA8]">{g.items.length}</span>
            </div>
            <ul className="space-y-1">
              {g.items.map((r) => (
                <li key={r.key}>
                  <button
                    onClick={() => toggle(r)}
                    aria-pressed={r.checked}
                    aria-label={`${r.checked ? 'Batal selesai' : 'Tandai selesai'}: ${r.title}`}
                    className="w-full flex items-center gap-2.5 min-h-[44px] px-1.5 -mx-1.5 rounded-lg text-left hover:bg-[#F7F6F2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] transition-colors"
                  >
                    <span
                      className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                        r.checked ? 'bg-[#15803D] border-[#15803D]' : 'bg-white border-[#CFCEC7]'
                      }`}
                    >
                      {r.checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block text-[13px] font-body truncate ${
                          r.checked ? 'text-[#AEADA8] line-through' : 'text-[#1A1916]'
                        }`}
                      >
                        {r.title}
                      </span>
                      <span className="flex items-center gap-1.5 mt-0.5">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.projectColor }} />
                        <span className="text-[11px] font-body text-[#8A8A85] truncate">{r.projectName}</span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}
